import { get } from "lodash-es";
import { useEffect, useState } from "react";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import type { FormInputProps } from "@/components/Form/FormInput";
import type { GroupIconType } from "@/components/GroupIcon/GroupIcon";
import { GroupIconPicker } from "@/components/GroupIcon/GroupIconPicker";
import { GroupIconMedia } from "@/components/Media/IconMedia";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { HiddenInput } from "@/design-system/HiddenInput";
import { isDefined } from "@/helpers/util";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { FormErrorWrapper } from "./FormErrorWrapper";

interface FormIconPickerProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends FormInputProps<TFormValues, TName> {
  titleText?: string;
  openText?: string;
  cancelText?: string;
  selectText?: string;
}

export function FormGroupIconPicker<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({ name, ...props }: FormIconPickerProps<TFormValues, TName>): React.ReactNode {
  const { t } = useTranslation();
  const { register, setValue } = useFormContext<TFormValues>();
  const { errors } = useFormState<TFormValues>();
  const selectedIcon = (useWatch({ name }) as GroupIconType) || "";
  const error = get(errors, name);
  const [modalIcon, setModalCharIcon] = useState<GroupIconType | null>(null);
  const [isOpen, modalOpenHandlers] = useBool(false);

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setModalCharIcon(selectedIcon);
  }, [selectedIcon]);

  function openModal() {
    modalOpenHandlers.setTrue();
    setModalCharIcon(selectedIcon || null);
  }

  const title = props.titleText || t("component.group-icon-picker-input.title");

  return (
    <FormErrorWrapper width="fit" name={name} data-testid={props["data-testid"]}>
      <GroupIconMedia
        icon={selectedIcon}
        onAdd={openModal}
        onEdit={openModal}
        aria-invalid={isDefined(error)}
        data-testid="icon-char-media"
        data-edit-testid="icon-char-action-open"
        data-add-testid="icon-char-action-open"
      />
      <HiddenInput readOnly {...register(name, props.rules)} />
      <Modal.Root title={title} isOpened={isOpen} onOpenChange={modalOpenHandlers.set}>
        <Modal.Body>
          <div className="h-scroll-area-compact w-full overflow-y-auto rounded-md border border-border-secondary">
            <Box padding="md">
              <GroupIconPicker selectedIcon={modalIcon} onChange={setModalCharIcon} />
            </Box>
          </div>
        </Modal.Body>
        <Modal.Actions>
          <Button
            type="button"
            variant="outline"
            tone="primary"
            onClick={() => {
              setModalCharIcon(null);
              modalOpenHandlers.setFalse();
            }}
          >
            {props.cancelText || t("common.action.cancel")}
          </Button>
          <Button
            type="button"
            variant="filled"
            disabled={modalIcon == null}
            onClick={() => {
              if (modalIcon != null) {
                setValue(name, modalIcon as any, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                });

                modalOpenHandlers.setFalse();
              }
            }}
            data-testid="icon-char-action-submit"
          >
            {props.selectText || t("common.action.select")}
          </Button>
        </Modal.Actions>
      </Modal.Root>
    </FormErrorWrapper>
  );
}
